import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import PhotoPresetCard from '../components/catalog/PhotoPresetCard';
import VideoPresetCard from '../components/catalog/VideoPresetCard';
import { getCardTitleImageWidth } from '../components/catalog/titleImageSizing';
import useBootstrap from '../hooks/useBootstrap';
import { useCatalogRubricRows } from '../core/queries/catalogRows';
import { useCatalog } from '../hooks/useCatalog';
import { localizedField } from '../locales/content';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import PhotoPickSheet from '../components/catalog/PhotoPickSheet';
import HorizontalScrollRow from '../components/catalog/HorizontalScrollRow';
import HomeSeeAllCard from '../components/home/HomeSeeAllCard';
import {
  buildSectionRowItems,
  isSeeAllRowItem,
} from '../components/catalog/sectionRowItems';
import { getPhotoCount } from '../utils/photoReferences';
import { MediaLoadingSpinner } from '../components/primitives';
import { hapticSelection } from '../utils/haptics';
import useConsentGuard from '../hooks/useConsentGuard';

/**
 * PhotoCatalog — страница `/photo-catalog`.
 *
 * Figma source: `318:4525` generate-image (402×809).
 * Структура (figma-spec §3 + симметрично VideoCatalog):
 *   - `HomeStatusBar` (abs top) + Body (pt-30, gap-section):
 *     - Стек горизонтальных каруселей `PhotoPresetCard`-ов по секциям.
 *       Каждая секция = preset-группа (`section.name` + `section.presets`),
 *       горизонтальный scroll-ряд карточек w=160px (aspect 3:4 → h=240px),
 *       gap 8. Identично VideoCatalog для UX-симметрии.
 *     - Skeleton — те же горизонтальные ряды с заглушками 160×240.
 *     - Empty state — «Пресеты отсутствуют».
 *
 * API shape (backend, contentService.getCatalog('photo')):
 *   GET /api/content/catalog/photo → { items: [{ id, name, cover, isNew, presets: [...] }] }
 *   Section fields: `name` (не `title`), `presets` (не `items`). Preset fields:
 *   `image`, `name`, `id`, `isNew`. Подтверждено через
 *   curl http://localhost:3002/api/content/catalog/photo (commit c1e5fa8).
 *
 * История: до этого фикса PhotoCatalog мапил `catSection.title`/`catSection.items`,
 * которых нет в реальном backend payload — каждая секция падала в `null` ветку
 * HomeSection-а и страница рендерилась пустой. MSW фикстуры зеркалили старый
 * (несуществующий) shape, из-за чего unit-тесты проходили при сломанной
 * проде. Теперь PhotoCatalog читает `section.name` + `section.presets[]`
 * напрямую, как VideoCatalog.
 */

function cardWidthClass(cardSize) {
  const value = String(cardSize || 'M').toUpperCase();
  if (value === 'L') return 'w-40';
  if (value === 'S') return 'w-28';
  return 'w-36';
}

function cardWidthPx(cardSize) {
  const value = String(cardSize || 'M').toUpperCase();
  if (value === 'L') return 160;
  if (value === 'S') return 112;
  return 144;
}

const INITIAL_PHOTO_RUBRIC_ROWS = 2;
const PHOTO_RUBRIC_ROWS_BATCH = 3;

function buildSkeletonRowFromRubric(rubric) {
  const cardSize = String(rubric?.cardSize || 'M').toUpperCase();
  return {
    id: rubric?.slug || rubric?.id || null,
    title: rubric?.label || rubric?.name || '',
    cardSize,
    order: rubric?.order,
    presets: Array.from({ length: cardSize === 'L' ? 2 : 3 }, (_, index) => ({ id: index + 1 })),
  };
}

function buildSkeletonRowFromSection(section) {
  const cardSize = String(section?.cardSize || 'M').toUpperCase();
  const presets = Array.isArray(section?.presets) ? section.presets : [];
  return {
    id: section?.id || null,
    title: localizedField(section, 'name') || section?.name || '',
    cardSize,
    order: section?.order,
    presets: presets.length > 0
      ? presets
      : Array.from({ length: cardSize === 'L' ? 2 : 3 }, (_, index) => ({ id: index + 1 })),
  };
}

function compareCatalogRows(a, b) {
  const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : Number.MAX_SAFE_INTEGER;
  const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

function PhotoCatalogSkeleton({
  photoRubrics = [],
  videoRubrics = [],
  photoRows = [],
  videoRows = [],
}) {
  const shimmerStyle = {
    '--skeleton-base': '#151517',
  };
  const titleShimmerStyle = {
    '--skeleton-base': '#171719',
  };
  const knownRows = [
    ...videoRows.filter((section) => Array.isArray(section?.presets) && section.presets.length > 0)
      .map(buildSkeletonRowFromSection),
    ...photoRows.filter((section) => Array.isArray(section?.presets) && section.presets.length > 0)
      .map(buildSkeletonRowFromSection),
  ].sort(compareCatalogRows);
  const fallbackRows = [
    ...videoRubrics.map(buildSkeletonRowFromRubric),
    ...photoRubrics.map(buildSkeletonRowFromRubric),
  ].sort(compareCatalogRows);
  const rows = knownRows.length > 0
    ? knownRows
    : fallbackRows.length > 0
      ? fallbackRows
    : [
        { id: 'fallback-1', title: '', cardSize: 'L', presets: [{ id: 1 }, { id: 2 }] },
        { id: 'fallback-2', title: '', cardSize: 'S', presets: [{ id: 1 }, { id: 2 }, { id: 3 }] },
        { id: 'fallback-3', title: '', cardSize: 'WIDE', presets: [{ id: 1 }] },
        { id: 'fallback-4', title: '', cardSize: 'M', presets: [{ id: 1 }, { id: 2 }, { id: 3 }] },
      ];

  return (
    <div className="min-h-screen bg-bg-app text-white" data-testid="photo-skeleton">
      <div className="pt-30 pb-12 flex flex-col gap-gap-section">
        {rows.map((section, sectionIndex) => {
          const cardSize = String(section.cardSize || 'M').toUpperCase();
          const cards = Array.isArray(section.presets) ? section.presets : [];
          const width = cardWidthClass(cardSize);
          const isWide = cardSize === 'WIDE';
          const title = String(section.title || '').trim();
          const titleWidth = title
            ? `${Math.max(96, Math.min(220, title.length * 9))}px`
            : '176px';
          return (
          <section
            // eslint-disable-next-line react/no-array-index-key
            key={section.id || sectionIndex}
            className="flex flex-col gap-gap-title-cards"
            data-role="catalog-skeleton-section"
          >
            <div
              className="mx-4 h-5 rounded-md skeleton-shimmer"
              style={{ ...titleShimmerStyle, width: titleWidth }}
            />
            {isWide ? (
              <div className="px-4">
                <div
                  className="h-[188px] w-full rounded-home-16 skeleton-shimmer"
                  style={shimmerStyle}
                />
              </div>
            ) : (
              <div className="flex flex-row items-stretch gap-gap-cards overflow-hidden px-4">
                {cards.map((_, cardIndex) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={cardIndex}
                    className={`flex-shrink-0 ${width} rounded-home-16 skeleton-shimmer`}
                    style={{ ...shimmerStyle, aspectRatio: '3 / 4' }}
                  />
                ))}
              </div>
            )}
          </section>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeader({ title, onSeeAll }) {
  const { t } = useTranslation();
  if (!title) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4">
      <h2 className="font-sans text-h2 text-text-primary" style={{ opacity: 0.9 }}>
        {title}
      </h2>
      {onSeeAll && (
        <button
          type="button"
          onClick={onSeeAll}
          className="flex shrink-0 items-center gap-2 font-sans text-sm font-semibold text-text-primary"
          data-testid="section-see-all"
        >
          <span>{t('common.seeAll')}</span>
          <span aria-hidden className="text-[20px] leading-none">→</span>
        </button>
      )}
    </div>
  );
}

function isWideCatalogPreset(preset) {
  return preset?.cardLayout === 'wide' && Boolean(preset?.wideImage);
}

function buildWideCatalogRows(presets) {
  const rows = [];
  let pendingNormal = [];

  const flushNormal = () => {
    if (pendingNormal.length > 0) {
      rows.push({ type: 'normal', items: pendingNormal });
      pendingNormal = [];
    }
  };

  for (const preset of presets || []) {
    if (isWideCatalogPreset(preset)) {
      flushNormal();
      rows.push({ type: 'wide', preset });
      continue;
    }
    pendingNormal.push(preset);
  }

  flushNormal();
  return rows;
}

function CatalogRow({
  section,
  type,
  onSeeAll,
  onPhotoSelect,
}) {
  const presets = section?.presets || [];
  if (!section || presets.length === 0) return null;

  const cardSize = String(section.cardSize || 'M').toUpperCase();
  const cardWidth = cardWidthClass(cardSize);
  const itemWidth = cardWidthPx(cardSize);
  const rowClass = 'flex flex-row items-stretch gap-gap-cards px-4';
  const rowItems = buildSectionRowItems(presets, {
    canSeeAll: Boolean(onSeeAll),
    markerId: `${type}-${section.id}`,
  });
  const renderPreset = (preset) => (
    type === 'video' ? (
      <VideoPresetCard preset={preset} cardSize={cardSize} />
    ) : (
      <PhotoPresetCard preset={preset} onSelect={onPhotoSelect} cardSize={cardSize} />
    )
  );

  if (type !== 'video' && cardSize === 'WIDE') {
    const wideRows = buildWideCatalogRows(presets);
    const normalCardSize = 'M';
    const normalCardWidth = cardWidthClass(normalCardSize);
    const normalItemWidth = cardWidthPx(normalCardSize);

    return (
      <section
        className="flex flex-col gap-gap-title-cards"
        data-testid="photo-section"
      >
        <SectionHeader title={localizedField(section, 'name')} onSeeAll={onSeeAll} />
        <div className="flex flex-col gap-gap-cards">
          {wideRows.map((row, rowIndex) => (
            row.type === 'wide' ? (
              <WidePhotoPresetCard
                key={row.preset.id}
                preset={row.preset}
                onSelect={onPhotoSelect}
              />
            ) : (
              <HorizontalScrollRow
                // eslint-disable-next-line react/no-array-index-key
                key={`normal-${rowIndex}`}
                data-testid={`catalog-row-photo-${section.id}-wide-fallback-${rowIndex}`}
                className={rowClass}
                items={row.items}
                itemWidth={normalItemWidth}
                initialItemCount={4}
                placeholderHeight={Math.round(normalItemWidth * 1.5)}
                renderItem={(preset) => (
                  <div className={`flex-shrink-0 ${normalCardWidth}`}>
                    <PhotoPresetCard
                      preset={preset}
                      onSelect={onPhotoSelect}
                      cardSize={normalCardSize}
                    />
                  </div>
                )}
              />
            )
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      className="flex flex-col gap-gap-title-cards"
      data-testid={type === 'video' ? 'mixed-video-section' : 'photo-section'}
    >
      <SectionHeader title={localizedField(section, 'name')} onSeeAll={onSeeAll} />
      <HorizontalScrollRow
        data-testid={`catalog-row-${type}-${section.id}`}
        className={rowClass}
        items={rowItems}
        itemWidth={(item) => (isSeeAllRowItem(item) ? 80 : itemWidth)}
        initialItemCount={4}
        placeholderHeight={Math.round(itemWidth * 1.5)}
        renderItem={(preset) =>
          isSeeAllRowItem(preset) ? (
            <HomeSeeAllCard
              onClick={onSeeAll}
              height={Math.round(itemWidth * 1.5)}
            />
          ) : (
            <div className={`flex-shrink-0 ${cardWidth}`}>
              {renderPreset(preset)}
            </div>
          )
        }
      />
    </section>
  );
}

function WidePhotoPresetCard({ preset, onSelect }) {
  if (!preset) return null;
  const name = localizedField(preset, 'name') || preset.name;
  const imageUrl = preset.wideImage || preset.image;
  const titleImage = preset.titleImage || preset.logoUrl || null;
  const [isImageLoaded, setIsImageLoaded] = useState(() => !imageUrl);

  useEffect(() => {
    setIsImageLoaded(!imageUrl);
  }, [imageUrl]);

  return (
    <section className="px-4" data-testid="wide-photo-preset">
      <button
        type="button"
        onClick={() => onSelect?.(preset)}
        className="relative h-[188px] w-full overflow-hidden rounded-home-16 bg-surface-2 text-left"
        aria-label={name || 'Photo preset'}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name || ''}
            className={[
              'absolute inset-0 h-full w-full object-cover transition-opacity duration-200',
              isImageLoaded ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
            loading="lazy"
            decoding="async"
            onLoad={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(true)}
          />
        ) : (
          <div className="absolute inset-0 bg-surface-2" />
        )}
        {!isImageLoaded && <MediaLoadingSpinner />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5">
          {titleImage ? (
            <img
              src={titleImage}
              alt=""
              className="h-auto max-w-full object-contain pointer-events-none"
              style={{ width: getCardTitleImageWidth('WIDE') }}
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          ) : (
            <h2 className="max-w-[60%] font-sans text-[30px] font-bold leading-[32px] text-text-primary">
              {name}
            </h2>
          )}
          <span className="flex h-10 shrink-0 items-center justify-center rounded-full bg-white px-5 font-sans text-sm font-bold text-black">
            Try Now <span className="ml-2 text-lg leading-none">→</span>
          </span>
        </div>
      </button>
    </section>
  );
}

export default function PhotoCatalog() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLocale = i18n.resolvedLanguage || i18n.language;
  const {
    data: bootstrap,
    skeletonData: bootstrapSkeletonData,
    loading: bootstrapLoading,
    fetching: bootstrapFetching,
    error: bootstrapError,
    refetch: refetchBootstrap,
  } = useBootstrap();
  const generateImage = useStore((state) => state.generateImage);
  const { requireConsent } = useConsentGuard();
  const [activePreset, setActivePreset] = useState(null);
  const [references, setReferences] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sheetError, setSheetError] = useState(null);
  const [visiblePhotoRubricCount, setVisiblePhotoRubricCount] = useState(0);
  const createInFlightRef = useRef(false);
  const bootstrapForLayout = bootstrap || bootstrapSkeletonData || null;

  const photoRubrics = useMemo(() => {
    const photoRubrics = bootstrapForLayout?.rubrics?.photo || [];
    return photoRubrics
      .filter((r) => r.slug !== 'all-photo' && !r.isSystem)
      .map((r) => ({
      slug: r.slug,
      label: localizedField(r, 'name') || r.title || r.label,
      cardSize: r.cardSize || 'M',
      order: r.order,
      sectionType: r.sectionType || 'PHOTO',
    }));
  }, [activeLocale, bootstrapForLayout]);

  const videoRubricsForRows = useMemo(() => {
    const videoRubrics = bootstrapForLayout?.rubrics?.video || [];
    return videoRubrics
      .filter((rubric) => rubric.slug !== 'all-video' && !rubric.isSystem)
      .map((rubric) => ({
        slug: rubric.slug,
        label: localizedField(rubric, 'name') || rubric.title || rubric.label,
        cardSize: rubric.cardSize || 'M',
        order: rubric.order,
        sectionType: rubric.sectionType || 'VIDEO',
      }));
  }, [activeLocale, bootstrapForLayout]);

  const { items: fallbackVideoSections, loading: fallbackVideoLoading } = useCatalog(
    'video',
    'all-video',
    { enabled: !bootstrapLoading && !bootstrapError && videoRubricsForRows.length === 0 }
  );
  const videoRubricQuery = useCatalogRubricRows('video', videoRubricsForRows, {
    enabled: !bootstrapLoading && !bootstrapError,
  });

  const visibleVideoSections = useMemo(
    () => {
      const source = videoRubricQuery.rows.length > 0 ? videoRubricQuery.rows : fallbackVideoSections;
      return (source || []).filter((section) => (section.presets || []).length > 0);
    },
    [fallbackVideoSections, videoRubricQuery.rows]
  );
  const photoRubricsForRows = useMemo(() => {
    return photoRubrics;
  }, [photoRubrics]);
  const photoRubricsWindowKey = useMemo(
    () => photoRubricsForRows.map((rubric) => rubric.slug).join('|'),
    [photoRubricsForRows]
  );
  const initialVisiblePhotoRubricCount = useMemo(
    () => Math.min(INITIAL_PHOTO_RUBRIC_ROWS, photoRubricsForRows.length),
    [photoRubricsForRows.length]
  );
  const effectiveVisiblePhotoRubricCount = useMemo(
    () => Math.min(
      photoRubricsForRows.length,
      Math.max(visiblePhotoRubricCount, initialVisiblePhotoRubricCount),
    ),
    [initialVisiblePhotoRubricCount, photoRubricsForRows.length, visiblePhotoRubricCount]
  );

  useEffect(() => {
    setVisiblePhotoRubricCount(
      initialVisiblePhotoRubricCount
    );
  }, [initialVisiblePhotoRubricCount, photoRubricsWindowKey]);

  const visiblePhotoRubricsForRows = useMemo(
    () => photoRubricsForRows.slice(0, effectiveVisiblePhotoRubricCount),
    [effectiveVisiblePhotoRubricCount, photoRubricsForRows]
  );

  const revealMorePhotoRubrics = useCallback(() => {
    if (effectiveVisiblePhotoRubricCount >= photoRubricsForRows.length) return;
    if (typeof window === 'undefined') return;

    const doc = document.documentElement;
    const scrollTop = window.scrollY || window.pageYOffset || doc?.scrollTop || 0;
    if (scrollTop <= 1) return;

    const viewportHeight = window.innerHeight || doc?.clientHeight || 0;
    const scrollHeight = doc?.scrollHeight || document.body?.scrollHeight || 0;
    const remaining = scrollHeight - (scrollTop + viewportHeight);

    if (remaining > viewportHeight * 1.25) return;

    setVisiblePhotoRubricCount((current) =>
      Math.min(photoRubricsForRows.length, current + PHOTO_RUBRIC_ROWS_BATCH)
    );
  }, [effectiveVisiblePhotoRubricCount, photoRubricsForRows.length]);

  useEffect(() => {
    if (effectiveVisiblePhotoRubricCount >= photoRubricsForRows.length) return undefined;
    if (typeof window === 'undefined') return undefined;

    let frame = 0;
    const scheduleReveal = () => {
      if (typeof window.requestAnimationFrame === 'function') {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(revealMorePhotoRubrics);
      } else {
        revealMorePhotoRubrics();
      }
    };

    window.addEventListener('scroll', scheduleReveal, { passive: true });
    window.addEventListener('resize', scheduleReveal);
    scheduleReveal();

    return () => {
      if (typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', scheduleReveal);
      window.removeEventListener('resize', scheduleReveal);
    };
  }, [effectiveVisiblePhotoRubricCount, photoRubricsForRows.length, revealMorePhotoRubrics]);

  const photoRubricQuery = useCatalogRubricRows('photo', visiblePhotoRubricsForRows, {
    enabled: !bootstrapLoading && !bootstrapError,
  });

  // scroll-to-top при монтировании (handoff §6).
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* jsdom does not implement scrollTo — non-fatal in tests */
      }
    }
  }, []);

  const loadingRows =
    photoRubricQuery.isLoading ||
    videoRubricQuery.isLoading ||
    fallbackVideoLoading;
  const fetchingRows =
    photoRubricQuery.isFetching ||
    videoRubricQuery.isFetching ||
    fallbackVideoLoading;
  const hasSections = photoRubricQuery.rows.length > 0 || visibleVideoSections.length > 0;
  const visiblePhotoRows = photoRubricQuery.rows;
  const visibleCatalogRows = useMemo(
    () => [
      ...visibleVideoSections.map((section) => ({ section, type: 'video' })),
      ...visiblePhotoRows.map((section) => ({ section, type: 'photo' })),
    ].sort((a, b) => compareCatalogRows(a.section, b.section)),
    [visiblePhotoRows, visibleVideoSections]
  );
  const rubricErrors = [...videoRubricQuery.errors, ...photoRubricQuery.errors];
  const requiredRefCount = activePreset ? getPhotoCount(activePreset) : 1;
  const [initialCatalogReady, setInitialCatalogReady] = useState(false);
  const closeSheet = useCallback(() => {
    if (generating) return;
    setActivePreset(null);
    setReferences([]);
    setSheetError(null);
  }, [generating]);

  const openSheet = useCallback((preset) => {
    setActivePreset(preset);
    setReferences([]);
    setSheetError(null);
  }, []);

  const performCreate = useCallback(async ({ prompt = '', batchCount = 1 } = {}) => {
    if (createInFlightRef.current) return;
    if (!activePreset) return;
    const readyRefs = references.filter(Boolean);
    if (readyRefs.length < requiredRefCount) return;

    createInFlightRef.current = true;
    setGenerating(true);
    setSheetError(null);
    navigate('/gallery');
    try {
      await generateImage(activePreset.id, prompt, '9:16', '1.5K', batchCount, readyRefs);
    } catch (error) {
      setSheetError(t('prompt.genError', 'Generation error'));
    } finally {
      createInFlightRef.current = false;
      setGenerating(false);
    }
  }, [activePreset, generateImage, navigate, references, requiredRefCount, t]);

  const handleCreate = useCallback(
    (options = {}) => requireConsent(() => performCreate(options), 'generate'),
    [performCreate, requireConsent],
  );

  useEffect(() => {
    if (bootstrapError) {
      setInitialCatalogReady(true);
      return;
    }

    if (bootstrapLoading || bootstrapFetching) return;
    if (fetchingRows) return;

    setInitialCatalogReady(true);
  }, [
    bootstrapError,
    bootstrapFetching,
    bootstrapLoading,
    fetchingRows,
  ]);

  if (!initialCatalogReady || bootstrapLoading || (loadingRows && !hasSections)) {
    return (
      <PhotoCatalogSkeleton
        photoRubrics={visiblePhotoRubricsForRows}
        videoRubrics={videoRubricsForRows}
        photoRows={visiblePhotoRows}
        videoRows={visibleVideoSections}
      />
    );
  }

  if (bootstrapError) {
    return (
      <div className="min-h-screen bg-bg-app text-white flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-text-soft">
          {t('home.loadError', 'Failed to load data')}
        </p>
        <button
          type="button"
          onClick={() => refetchBootstrap()}
          className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-black"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-app text-white">
      <div className="pt-30 pb-12 flex flex-col gap-gap-section">
        <div className="flex flex-col gap-gap-section">
          {rubricErrors.length > 0 && (
            <div
              className="mx-4 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100"
              role="alert"
            >
              <div className="mb-3">
                {t('home.loadError', 'Failed to load data')}
              </div>
              <button
                type="button"
                onClick={() => {
                  videoRubricQuery.refetch();
                  photoRubricQuery.refetch();
                }}
                className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-black"
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {!loadingRows && !hasSections && (
            <div className="text-text-mute text-center py-20 px-4">
              {t('gallery.emptyCatalog')}
            </div>
          )}

          {visibleCatalogRows.map(({ section, type }) => (
            <CatalogRow
              key={`${type}-${section.id}`}
              section={section}
              type={type}
              onSeeAll={() => {
                hapticSelection();
                navigate(
                  `/photo-catalog/section/${encodeURIComponent(section.id)}?type=${type}`
                );
              }}
              onPhotoSelect={type === 'photo' ? openSheet : undefined}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          hapticSelection();
          navigate('/search');
        }}
        className={[
          'fixed left-1/2 z-30 inline-flex -translate-x-1/2 items-center gap-[7.5px]',
          'rounded-home-20 px-[7.5px] py-[3.75px]',
          'bg-gradient-to-l from-glass-24-start to-glass-24-end',
          'font-sans font-medium text-[15px] leading-[17.61px] text-white',
          'backdrop-blur-glass-strong transition-transform active:scale-[0.97]',
        ].join(' ')}
        style={{
          bottom:
            'calc(var(--safe-bottom, 0px) + 96px + var(--search-bottom-offset, 0px))',
        }}
        aria-label={t('home.search', 'Поиск')}
        data-testid="photo-catalog-search-button"
      >
        <Search aria-hidden="true" size={22} strokeWidth={2.4} />
        <span>{t('home.search', 'Поиск')}</span>
      </button>

      <PhotoPickSheet
        preset={activePreset}
        references={references}
        requiredCount={requiredRefCount}
        loading={generating}
        error={sheetError}
        onClose={closeSheet}
        onReferencesChange={setReferences}
        onCreate={handleCreate}
      />
    </div>
  );
}
